<template>
    <!-- Campos del prospecto (las columnas del Excel), divididos en secciones:
         Empresa y Persona de contacto. v-model = objeto con las claves de CAMPOS;
         `errores` = { campo: requerido|correo|enlace|largo }.
         `numerar` = secciones numeradas con descripción (Nueva tarjeta); sin él,
         versión compacta (pestaña Datos del panel). El slot por defecto agrega
         más secciones al final (Nueva tarjeta: En el tablero, Primera nota) y
         `tras-empresa` va debajo del nombre (aviso de duplicada). -->
    <form class="cr-secs" :class="{ 'cr-secs--num': numerar }" novalidate @submit.prevent="$emit('enter')" @keydown.enter="enter">
        <datalist :id="pref + '-tipos'"><option v-for="tp in crm.tipos" :key="tp.v" :value="tp.v"></option></datalist>
        <section v-for="(g, gi) in GRUPOS" :key="g.k" class="cr-sec" :aria-labelledby="pref + '-g-' + g.k">
            <header class="cr-sec-h">
                <span v-if="numerar" class="cr-sec-n" :class="{ 'is-done': estadoGrupo(g).ok }" aria-hidden="true">
                    {{ gi + 1 }}
                </span>
                <span v-else class="cr-sec-ic" aria-hidden="true" v-html="I[g.ic]"></span>
                <div class="cr-sec-tt">
                    <h4 :id="pref + '-g-' + g.k">{{ crm.t('sec.' + g.k) }}</h4>
                    <p v-if="numerar">{{ crm.t('sec.' + g.k + '_p') }}</p>
                </div>
                <span class="cr-sec-st" :class="estadoGrupo(g).cls">{{ estadoGrupo(g).txt }}</span>
            </header>
            <div class="cr-form">
                <template v-for="f in camposDe(g)">
                    <div :key="f.k" class="cr-fld" :class="{ 'cr-fld--w': f.w, 'is-bad': !!errores[f.k], 'is-req': f.req }">
                        <label :for="pref + '-' + f.k">
                            <span class="cr-fld-ic" :class="{ 'is-in': f.ic === 'in' }" aria-hidden="true" v-html="I[f.ic]"></span>{{ crm.t('fld.' + f.k) }}
                            <span v-if="f.req" class="cr-req">{{ crm.t('sec.req') }}</span>
                        </label>
                        <input :id="pref + '-' + f.k" :ref="'f-' + f.k" :value="value[f.k]" :type="f.type === 'email' ? 'email' : 'text'" :list="f.list ? pref + '-tipos' : null"
                               :placeholder="f.ph || ''" :inputmode="f.type === 'url' ? 'url' : null" :maxlength="f.max" autocomplete="off"
                               :aria-required="f.req ? 'true' : null" :aria-invalid="errores[f.k] ? 'true' : null"
                               :aria-describedby="errores[f.k] ? pref + '-' + f.k + '-e' : f.hint ? pref + '-' + f.k + '-h' : null"
                               @input="cambiar(f.k, $event.target.value)" />
                        <span v-if="errores[f.k]" :id="pref + '-' + f.k + '-e'" class="cr-err">{{ crm.t('err.' + errores[f.k]) }}</span>
                        <span v-else-if="f.hint" :id="pref + '-' + f.k + '-h'" class="cr-hint">{{ crm.t('fld.hint_' + f.k) }}</span>
                    </div>
                    <div v-if="f.k === 'empresa' && $slots['tras-empresa']" :key="f.k + '-x'" class="cr-fld--w"><slot name="tras-empresa" /></div>
                </template>
            </div>
        </section>
        <slot />
    </form>
</template>

<script>
import { I, correoOk, urlOk } from './crmUtil';

export const CAMPOS = [
    { k: 'empresa', req: true, w: true, max: 191, ic: 'building', ph: 'Ej.: Ruzafa Living' },
    { k: 'tipo', list: true, max: 191, ic: 'tag', ph: 'Ej.: Coliving' },
    { k: 'ciudades', hint: true, max: 500, ic: 'pin', ph: 'Ej.: Madrid; Valencia' },
    { k: 'web', type: 'url', ph: 'https://', max: 500, ic: 'web' },
    { k: 'liE', type: 'url', ph: 'https://linkedin.com/company/…', max: 500, ic: 'in' },
    { k: 'contacto', max: 191, ic: 'user', ph: 'Nombre y apellido' },
    { k: 'cargo', max: 191, ic: 'brief', ph: 'Ej.: Gerente comercial' },
    { k: 'email', type: 'email', max: 191, ic: 'mail', ph: 'nombre@empresa.com' },
    { k: 'liP', type: 'url', ph: 'https://linkedin.com/in/…', max: 500, ic: 'in' },
];

/** Secciones (en este orden). */
export const GRUPOS = [
    { k: 'empresa', ic: 'building', campos: ['empresa', 'tipo', 'ciudades', 'web', 'liE'] },
    { k: 'contacto', ic: 'user', campos: ['contacto', 'cargo', 'email', 'liP'] },
];

/** Misma validación que la API (CrmAnfitrionesController::validarDatos). */
export function validar(v) {
    const err = {};
    if (!String(v.empresa || '').trim()) err.empresa = 'requerido';
    const mail = String(v.email || '').trim();
    if (mail && !correoOk(mail)) err.email = 'correo';
    ['web', 'liP', 'liE'].forEach(k => { const s = String(v[k] || '').trim(); if (s && !urlOk(s)) err[k] = 'enlace'; });
    return err;
}
/** Errores 422 de la API → { campo: motivo } (o null si no son de validación). */
export function erroresApi(e) {
    const r = e && e.response;
    return r && r.status === 422 && r.data && r.data.errores ? r.data.errores : null;
}

const POR_K = {}; CAMPOS.forEach(f => { POR_K[f.k] = f; });

export default {
    name: 'CrmCampos',
    inject: ['crm'],
    props: {
        value: { type: Object, required: true },
        errores: { type: Object, default: () => ({}) },
        pref: { type: String, default: 'cf' },
        numerar: { type: Boolean, default: false },
    },
    data() { return { I, GRUPOS }; },
    methods: {
        camposDe(g) { return g.campos.map(k => POR_K[k]); },
        /** Cartelito de la derecha de cada sección: falta lo obligatorio / n de t / completo. */
        estadoGrupo(g) {
            const lleno = k => !!String(this.value[k] || '').trim();
            const n = g.campos.filter(lleno).length, total = g.campos.length;
            if (g.campos.some(k => POR_K[k].req && !lleno(k))) return { ok: false, cls: 'is-warn', txt: this.crm.t('sec.missing') };
            if (n === total) return { ok: true, cls: 'is-ok', txt: this.crm.t('sec.done') };
            return { ok: n > 0, cls: '', txt: this.crm.t('sec.filled', { n, t: total }) };
        },
        cambiar(k, v) { this.$emit('input', { ...this.value, [k]: v }); },
        enfocar(k) { this.$nextTick(() => { const el = this.$refs['f-' + k]; if (el && el[0]) { el[0].focus(); el[0].scrollIntoView({ block: 'nearest' }); } }); },
        /** Enter en un campo de texto = enviar. En textarea es salto de línea y en
         *  botones (desplegables, prioridad) hace lo suyo. */
        enter(e) {
            const tg = e.target.tagName;
            if (tg === 'TEXTAREA' || tg === 'BUTTON' || e.isComposing) return;
            e.preventDefault();
            this.$emit('enter');
        },
    },
};
</script>
